import React from 'react';
import type { ISupraManagedAccessRecordsByUser } from '@/app-package';
import styled from 'styled-components';
import { Text, Flex, Icon, Color } from '@bb/elements-web';
import { Avatar } from 'components';
import { UserSubRoles } from '@bb/users';
import { Space } from 'antd';

type ManagedAccessRecordRowProps = {
  record: ISupraManagedAccessRecordsByUser;
  showGrantee?: boolean; // whether to show the grantee info (avatar and name)
};

/**
 * Formats a date range for display.
 * ex "Mon, Jan 1 - Wed, Jan 3" or "Mon, Jan 1" if single day
 * @param startDateTime - The start date and time in ISO format.
 * @param endDateTime - The end date and time in ISO format.
 * @returns A formatted date range string.
 */
const formatDateRange = (
  startDateTime: string,
  endDateTime: string,
): string => {
  const [start, end] = [new Date(startDateTime), new Date(endDateTime)].map(d =>
    d.toLocaleDateString('en-US', {
      weekday: 'short',
      month: 'short',
      day: 'numeric',
    }),
  );

  return start === end ? start : `${start} - ${end}`;
};

/**
 * Formats a time range for display. ex "1:00pm - 2:00pm"
 * @param startDateTime - The start date and time in ISO format.
 * @param endDateTime - The end date and time in ISO format.
 * @returns A formated time range string.
 */
const formatTimeRange = (
  startDateTime: string,
  endDateTime: string,
): string => {
  const [start, end] = [new Date(startDateTime), new Date(endDateTime)].map(d =>
    d
      .toLocaleTimeString('en-US', {
        hour: 'numeric',
        minute: '2-digit',
        hour12: true,
      })
      .toLowerCase()
      .replace(' ', ''),
  );

  return `${start} - ${end}`;
};

/**
 * A component to represent a managed access record in the Managed Access Drawer.
 * @param props.record - The managed access record to display.
 * @param props.showGrantee - Whether to show the grantee information (avatar and name). Defaults to true.
 */
const ManagedAccessDrawerRow: React.FC<ManagedAccessRecordRowProps> = ({
  record,
  showGrantee = true,
}) => {
  const address = [record.listingLine1, record.listingLine2]
    .filter(Boolean)
    .join(', ');

  return (
    <StyledWrapper data-cy="managed-access-drawer-row">
      <Space size={8} direction="vertical" style={{ width: '100%' }}>
        <Flex gap={10} align="center">
          <StyledThumbnail>
            {record.listingThumbnail ? (
              <img src={record.listingThumbnail} alt={address} />
            ) : (
              <StyledThumbnailPlaceholder>
                <Icon icon="homeFilled" size={16} color="grey" />
              </StyledThumbnailPlaceholder>
            )}
          </StyledThumbnail>
          <Text style={{ fontWeight: 500 }} color="PRIMARY_TEXT">
            {address.length > 35 ? `${address.slice(0, 35)}...` : address}
          </Text>
        </Flex>

        <StyledRecordContent>
          <StyledTextWrapper>
            <Icon icon="calendarOutlined" size={16} />
            {formatDateRange(record.startDateTime, record.endDateTime)}
          </StyledTextWrapper>
          <StyledTextWrapper>
            <Icon icon="clockCircleOutlined" size={16} />
            {formatTimeRange(record.startDateTime, record.endDateTime)}
          </StyledTextWrapper>
        </StyledRecordContent>

        {showGrantee && (
          <Flex gap={8} align="center">
            <Avatar
              user={{
                firstName: record.granteeFirstName,
                lastName: record.granteeLastName,
                avatarFilestackId: record.granteeAvatarFilestackId,
              }}
            />
            <Text>
              <span style={{ fontWeight: 500 }}>
                {record.granteeFirstName} {record.granteeLastName}
              </span>{' '}
              &#x00B7;{' '}
              {record.granteeSubRole === UserSubRoles['Single Access']
                ? // if it's a single access user, show the company name in the grantee's extended profile, otherwise show the org name
                  record.granteeExtendedProfile?.companyName
                : record.granteeOrgName}
            </Text>
          </Flex>
        )}
      </Space>
    </StyledWrapper>
  );
};

export default ManagedAccessDrawerRow;

const StyledWrapper = styled.div`
  border: 0.5px solid ${Color.BORDER_GREY};
  border-radius: 8px;
  padding: 8px 16px;
  display: flex;
  flex-direction: column;
  gap: 8px;
  flex: 1;
`;

const StyledThumbnailPlaceholder = styled.div`
  width: 100%;
  height: 100%;
  background-color: ${Color.BEERUS};
  display: flex;
  justify-content: center;
  align-items: center;
`;

const StyledThumbnail = styled.div`
  flex-shrink: 0;
  width: 36px;
  height: 36px;
  border-radius: 2px;
  overflow: hidden;
  display: flex;
  flex-direction: column;

  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
  }
`;

const StyledRecordContent = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2px;
  flex: 1;
  min-width: 0;
`;

const StyledTextWrapper = styled.p`
  display: flex;
  gap: 9px;
  align-items: center;
  margin: 0;
  color: ${Color.BODY_TEXT};
`;
